import { formatInTimeZone } from 'date-fns-tz';

/**
 * NFR-10: everything on the wire is UTC; everything on screen is rendered in the
 * business timezone. Crucially there is no `toISOString().split('T')[0]`
 * anywhere -- that is what shifted evening bookings to the previous day in the
 * legacy slot picker at +02:00.
 */

export const BUSINESS_TIMEZONE = 'Africa/Cairo';

let timezone = BUSINESS_TIMEZONE;

/** The BFF tells us the configured timezone at boot, so both agree. */
export function setTimezone(tz: string) {
  if (tz) timezone = tz;
}

export function getTimezone(): string {
  return timezone;
}

function safeDate(value: string | Date | undefined | null): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A date with no time, e.g. 05 Feb 2026. */
export function formatDate(value: string | Date | undefined | null, fallback = '—'): string {
  const d = safeDate(value);
  return d ? formatInTimeZone(d, timezone, 'dd MMM yyyy') : fallback;
}

/** A full instant, e.g. 05 Feb 2026, 14:30. Used wherever a time matters (APT-4). */
export function formatDateTime(value: string | Date | undefined | null, fallback = '—'): string {
  const d = safeDate(value);
  return d ? formatInTimeZone(d, timezone, 'dd MMM yyyy, HH:mm') : fallback;
}

export function formatTime(value: string | Date | undefined | null, fallback = '—'): string {
  const d = safeDate(value);
  return d ? formatInTimeZone(d, timezone, 'HH:mm') : fallback;
}

/**
 * The calendar day of an instant, in the business timezone, as YYYY-MM-DD.
 * This is the correct replacement for toISOString().split('T')[0].
 */
export function toBusinessDay(value: string | Date | undefined | null): string {
  const d = safeDate(value) ?? new Date();
  return formatInTimeZone(d, timezone, 'yyyy-MM-dd');
}

/** Today, in the business timezone. */
export function businessToday(): string {
  return toBusinessDay(new Date());
}

export function formatMoney(value: number | undefined | null, currency = 'EGP'): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-EG', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat().format(value);
}

/** Renders a KPI delta as a signed percentage; null means "no prior period". */
export function formatDelta(delta: number | null | undefined): string | null {
  if (delta === null || delta === undefined || !Number.isFinite(delta)) return null;
  const pct = Math.round(delta * 100);
  return `${pct >= 0 ? '+' : ''}${pct}%`;
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

/** REQ-1: long ObjectIds are shown truncated, with copy-to-clipboard alongside. */
export function truncateId(id: string | undefined, keep = 6): string {
  if (!id) return '—';
  return id.length <= keep * 2 ? id : `${id.slice(0, keep)}…${id.slice(-4)}`;
}

export function fullName(party: {
  fName?: string;
  lName?: string;
} | undefined): string {
  if (!party) return '—';
  const name = [party.fName, party.lName].filter(Boolean).join(' ').trim();
  return name || '—';
}

export function arabicName(party: { fNameAr?: string; lNameAr?: string } | undefined): string {
  if (!party) return '—';
  const name = [party.fNameAr, party.lNameAr].filter(Boolean).join(' ').trim();
  return name || '—';
}

/** CLI-5 / LAW-1: null-safe address rendering, never "undefined undefined". */
export function formatAddress(
  address: { area?: string; city?: string; country?: string } | undefined,
): string {
  if (!address) return '—';
  const parts = [address.area, address.city, address.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

/** EMG-2: a map link with the coordinates in the right order. */
export function mapsUrl(location: { lat: number; lng: number } | undefined): string | null {
  if (!location) return null;
  return `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
}
